// src/pages/tasks/my-task-page/MyTaskPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Input,
  Upload,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  SendOutlined,
  CheckCircleOutlined,
  BellOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import moment from "moment-timezone";

const NZ_TIMEZONE = "Pacific/Auckland";
const toNZTime = (date: any) => date ? moment.utc(date).tz(NZ_TIMEZONE) : null;
import { useHistory } from "react-router-dom";
import { ApiRequest } from "../../../services/api/api";
import { getUserId } from "../../../services/lib/utils/auth.utils";
import {
  normalizeTaskCommentDisplayType,
  taskCommentTypeForApi,
} from "../task-comment-utils";
import SweetAlertService from "../../../services/lib/utils/sweet-alert-service";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

type Status = "Not Started" | "In Progress" | "Completed" | "Overdue";

const STATUS_COLOR_MAP: Record<Status, string> = {
  "Not Started": "default",
  "In Progress": "processing",
  "Completed": "success",
  Overdue: "error",
};

const normalizeStatus = (value: any): Status => {
  const text = String(value ?? "").trim().toLowerCase();
  switch (text) {
    case "in_progress":
    case "in progress":
    case "in-progress":
      return "In Progress";
    case "completed":
    case "complete":
    case "done":
      return "Completed";
    case "overdue":
    case "blocked":
    case "late":
      return "Overdue";
    case "not started":
    case "not_started":
    case "not-started":
    default:
      return "Not Started";
  }
};

type RowItem = {
  id: string;
  title: string;
  description?: string;
  role: "Manager" | "Assignee" | "Follower";
  manager: string;
  assignees: string[];
  followers?: string[];
  startDate?: string | null;
  dueDate?: string | null;
  latestComment?: string;
  status: Status;
  checkpoints?: { name: string; state: "Not Started" | "In Progress" | "Completed" }[];
  confirmedByMe?: boolean;
  confirmedCount?: number;
  assigneeTotal?: number;
  participants?: { userId: number; name: string; role: string }[];
  myConfirmed?: boolean; // 新增：当前用户是否已确认
};

type CommentType = "Routine" | "Apply Reschedule" | "Apply Completion";

// ===== Mock & API placeholders =====
const USE_MOCK = false;

async function apiListMyTasks(): Promise<RowItem[]> {
  if (USE_MOCK) {
    const data: RowItem[] = new Array(10).fill(0).map((_, i) => ({
      id: String(4000 + i),
      title: i % 3 === 0 ? "Please use progress update" : `Task ${i + 1}`,
      description: "Description text for this task. (demo)",
      role: (["Assignee", "Follower", "Manager"][i % 3] as RowItem["role"]),
      manager: "Alice",
      assignees: ["Bob", "Chris", ...(i % 2 ? ["Diana"] : [])],
      followers: ["Evan"],
      startDate: moment().add(-i, "day").toISOString(),
      dueDate: moment().add(5 + (i % 3), "day").toISOString(),
      latestComment:
        i % 2 === 0 ? "Apply Completion Text A / Completion Checkpoint A" : "Progress Update · Checkpoint B In Progress",
      status: (["Open", "InProgress", "Done", "Blocked"][i % 4] as Status),
      checkpoints: [
        { name: "Checkpoint A", state: i % 2 === 0 ? "Completed" : "In Progress" },
        { name: "Checkpoint B", state: "In Progress" },
      ],
      confirmedByMe: i % 4 === 1,
      confirmedCount: i % 3,
      assigneeTotal: 2 + (i % 2),
    }));
    return data;
  }
  const employeeIdRaw = getUserId();
  const employeeId = employeeIdRaw ? parseInt(String(employeeIdRaw), 10) : NaN;
  if (!employeeId) {
    throw new Error("No employee found in current session.");
  }
  const result = await ApiRequest({
    otherUrl: "TaskManagement/GetAllTasks",
    type: "get",
    params: { employeeId },
  });
  const payload = result?.data?.data ?? result?.data ?? [];
  const tasks = Array.isArray(payload) ? payload : payload?.data ?? [];
  return (tasks || []).map((task: any) => {
    const taskId = task.taskId ?? task.TaskId;
    const title = task.title ?? task.Title ?? "";
    const description = task.description ?? task.Description ?? "";
    const manager = task.manager ?? task.Manager ?? "";
    const managerId = task.managerId ?? task.ManagerId ?? null;
    const startDate = task.startDate ?? task.StartDate ?? null;
    const dueDate = task.dueDate ?? task.DueDate ?? null;
    const status = normalizeStatus(task.status ?? task.Status ?? "Not Started");
    const assignees = task.assignees ?? task.Assignees ?? [];
    const followers = task.followers ?? task.Followers ?? [];
    const participantsRaw = task.participants ?? task.Participants ?? [];
    const participants = (participantsRaw || [])
      .map((p: any) => {
        const userId = parseInt(String(p?.userId ?? p?.UserId ?? 0), 10);
        return {
          userId: Number.isFinite(userId) ? userId : 0,
          name: p?.name ?? p?.Name ?? "",
          role: (p?.role ?? p?.Role ?? "").toLowerCase(),
        };
      })
      .filter((p: any) => p.userId > 0);
    const myRoleEntry = participants.find((p: any) => p.userId === employeeId);
    const roleMapping: Record<string, RowItem["role"]> = {
      manager: "Manager",
      assignee: "Assignee",
      follower: "Follower",
    };
    const myRole =
      roleMapping[
        String(myRoleEntry?.role ?? myRoleEntry?.Role ?? "Follower").toLowerCase()
      ] || "Follower";

    const comments = task.comments ?? task.Comments ?? [];
    const latest = comments[0];
    const latestComment = latest
      ? `${normalizeTaskCommentDisplayType(
          latest.commentType ?? latest.CommentType ?? "",
          latest.content ?? latest.Content ?? ""
        )}: ${latest.content ?? latest.Content ?? ""}`
      : undefined;

    return {
      id: String(taskId),
      title,
      description,
      role: myRole,
      manager: manager || "-",
      assignees: assignees,
      followers: followers,
      startDate,
      dueDate,
      latestComment,
      status,
      checkpoints: (task.checkpoints ?? task.Checkpoints ?? []).map(
        (c: any) => ({
          name: c.name ?? c.Name ?? "",
          state: "In Progress",
        })
      ),
      participants,
    };
  });
}

async function apiConfirm(taskId: string, comment: string) {
  if (USE_MOCK) {
    console.log("[MOCK] confirm", { taskId });
    return { allConfirmed: Math.random() > 0.6 };
  }
  if (!currentUserNumericId) {
    throw new Error("Current user is not identified.");
  }
  const numericId = parseInt(taskId, 10);
  if (!numericId) {
    throw new Error("Invalid task id.");
  }
  await ApiRequest({
    otherUrl: "TaskManagement/ConfirmTask",
    method: "post",
    data: {
      taskId: numericId,
      confirmedBy: currentUserNumericId,
      comment,
    },
    isShowSpinner: true,
  });
  return { allConfirmed: true };
}

const currentUserIdValue = getUserId();
const currentUserNumericId = currentUserIdValue ? parseInt(String(currentUserIdValue), 10) : 0;

async function apiAddComment(taskId: string, type: CommentType, content: string, notifyUserIds: number[]) {
  if (USE_MOCK) {
    console.log("[MOCK] add comment", taskId, { type, content, notifyUserIds });
    return;
  }
  if (!currentUserNumericId) {
    throw new Error("Current user is not identified.");
  }
  const taskNumericId = parseInt(taskId, 10);
  if (!taskNumericId) {
    throw new Error("Invalid task id.");
  }
  await ApiRequest({
    otherUrl: "TaskComment/AddComment",
    method: "post",
    data: {
      taskId: taskNumericId,
      createdBy: currentUserNumericId,
      commentType: taskCommentTypeForApi(type),
      content,
      notifyUserIds,
    },
    isShowSpinner: true,
  });
}

async function apiCreateNotice(taskId: string, text: string, notifyUserIds: number[]) {
  if (USE_MOCK) {
    console.log("[MOCK] notice", { taskId, text, notifyUserIds });
    return;
  }
  if (!currentUserNumericId) {
    throw new Error("Current user is not identified.");
  }
  const taskNumericId = parseInt(taskId, 10);
  if (!taskNumericId) {
    throw new Error("Invalid task id.");
  }
  await ApiRequest({
    otherUrl: "TaskComment/AddComment",
    method: "post",
    data: {
      taskId: taskNumericId,
      createdBy: currentUserNumericId,
      commentType: "Notice",
      content: text,
      notifyUserIds,
    },
    isShowSpinner: true,
  });
}
// ====================================

const statusTag = (s: Status) => <Tag color={STATUS_COLOR_MAP[s]}>{s}</Tag>;

const fmt = (d?: string | null) => (d ? toNZTime(d)?.format("YYYY/MM/DD") : "-");

const MyTaskPage: React.FC = () => {
  const history = useHistory();

  const [rows, setRows] = useState<RowItem[]>([]);
  const [loading, setLoading] = useState(false);

  // add comment modal
  const [commentVisible, setCommentVisible] = useState(false);
  const [commentType, setCommentType] = useState<CommentType>("Routine");
  const [commentContent, setCommentContent] = useState("");
  const [attachments, setAttachments] = useState<{ name: string; url: string }[]>([]);
  const [activeRow, setActiveRow] = useState<RowItem | null>(null);

  // notice modal
  const [noticeVisible, setNoticeVisible] = useState(false);
  const [visibility, setVisibility] = useState<"All" | "Selected">("Selected");
  const [people, setPeople] = useState<string[]>([]);
  const [peopleOptions, setPeopleOptions] = useState<{ value: string; label: string }[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      const list = await apiListMyTasks();
      setRows(list);
      document.title = "My Task";
    } catch (e: any) {
      message.error(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const isManagerRow = useCallback(
    (row: RowItem) => {
      if (!row) return false;
      if (row.role === "Manager") return true;
      const participants = row.participants || [];
      return participants.some(
        (p) =>
          Number.isFinite(p.userId) &&
          p.userId === currentUserNumericId &&
          String(p.role ?? "").toLowerCase() === "manager"
      );
    },
    [currentUserNumericId]
  );

  const columns = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "title",
        render: (t: string, r: RowItem) => <a onClick={() => history.push(`/tasks/detail/${r.id}`)}>{t}</a>,
        width: 220,
      },
      {
        title: "Description",
        dataIndex: "description",
        render: (v?: string) => <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 2 }}>{v || "-"}</Paragraph>,
        width: 240,
      },
      { title: "Role", dataIndex: "role", width: 120, render: (v: RowItem["role"]) => <Tag>{v}</Tag> },
      {
        title: "Others",
        key: "others",
        width: 260,
        render: (_: any, r: RowItem) => {
          const currentUserParticipant = r.participants?.find(
            (p) => p.userId === currentUserNumericId
          );
          const currentUserName = currentUserParticipant?.name || "";
          
          const renderName = (name: string, shouldHighlight: boolean) => {
            if (shouldHighlight && r.myConfirmed) {
              return <span style={{ color: "#ff7a00", fontWeight: 600 }}>{name}</span>;
            }
            return <span>{name}</span>;
          };
          
          const isMyName = (name: string) => name === currentUserName && currentUserName !== "";
          
          return (
            <div style={{ lineHeight: 1.4 }}>
              <div>
                <Text type="secondary">Manager:</Text>{" "}
                {renderName(r.manager || "-", isMyName(r.manager || ""))}
              </div>
              <div>
                <Text type="secondary">Assignee:</Text>{" "}
                {r.assignees && r.assignees.length > 0 ? (
                  r.assignees.map((name, idx) => (
                    <span key={idx}>
                      {idx > 0 && ", "}
                      {renderName(name, isMyName(name))}
                    </span>
                  ))
                ) : (
                  "-"
                )}
              </div>
              {r.followers && r.followers.length > 0 && (
                <div>
                  <Text type="secondary">Follower:</Text>{" "}
                  {r.followers.map((name, idx) => (
                    <span key={idx}>
                      {idx > 0 && ", "}
                      {renderName(name, isMyName(name))}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
      { title: "Start", dataIndex: "startDate", width: 120, render: (d?: string) => fmt(d) },
      { title: "Due", dataIndex: "dueDate", width: 120, render: (d?: string) => fmt(d) },
      { title: "Comments", dataIndex: "latestComment", render: (v?: string) => v || "-" },
      {
        title: "Checklist Status",
        key: "ck",
        width: 230,
        render: (_: any, r: RowItem) =>
          r.checkpoints && r.checkpoints.length ? (
            <Space wrap>
              {r.checkpoints.map((c) => (
                <Badge
                  key={c.name}
                  status={c.state === "Completed" ? "success" : c.state === "In Progress" ? "processing" : "default"}
                  text={`${c.name} ${c.state}`}
                />
              ))}
            </Space>
          ) : ("-"),
      },
      {
        title: "Actions",
        key: "act",
        width: 280,
        render: (_: any, r: RowItem) => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <Space wrap>
              <Button size="small" icon={<EyeOutlined />} onClick={() => history.push(`/tasks/detail/${r.id}`)}>Details</Button>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  if (!isManagerRow(r)) {
                    SweetAlertService.warningMessage("Only managers can edit this task.", "Warning");
                    return;
                  }
                  history.push(`/tasks/${r.id}/edit`);
                }}
              >
                Edit
              </Button>
              <Button size="small" icon={<SendOutlined />} onClick={() => openAddComment(r)}>Progress</Button>
              <Button
                size="small"
                type={r.confirmedByMe ? "default" : "primary"}
                icon={<CheckCircleOutlined />}
                disabled={r.confirmedByMe}
                onClick={() => {
                  if (!isManagerRow(r)) {
                    SweetAlertService.warningMessage("Only managers can mark this task as completed.", "Warning");
                    return;
                  }
                  handleConfirm(r);
                }}
              >
                {r.confirmedByMe ? "Confirmed" : "Confirm"}
              </Button>
              <Button size="small" icon={<BellOutlined />} onClick={() => openNotice(r)}>Notice</Button>
            </Space>
            {!r.myConfirmed && (
              <Button
                size="small"
                type="primary"
                onClick={() => handleMyConfirm(r)}
              >
                Confirm
              </Button>
            )}
          </div>
        ),
      },
      { title: "Status", dataIndex: "status", width: 140, render: (s: Status) => statusTag(s) },
    ],
    [history, rows, isManagerRow]
  );

  const openAddComment = (row: RowItem) => {
    setActiveRow(row);
    setCommentType("Routine");
    setCommentContent("");
    setAttachments([]);
    setCommentVisible(true);
  };

  const handleAddComment = async () => {
    if (!activeRow) return;
    if (!commentContent.trim()) return message.warning("Please input content");
    try {
      const notifyIds =
        activeRow.participants
          ?.map((p) => p.userId)
          .filter((id) => Number.isFinite(id) && id > 0) ?? [];
      await apiAddComment(activeRow.id, commentType, commentContent.trim(), notifyIds);
      setCommentVisible(false);
      setNoticeVisible(true);
      message.success("Comment added");
    } catch (error: any) {
      console.error("Failed to add comment", error);
      const msg = error?.message;
      message.error(msg && msg !== "[object Object]" ? msg : "Failed to add comment");
    }
  };

  const handleConfirm = async (row: RowItem) => {
    try {
      const comment = `Confirmed task ${row.title}`;
      const { allConfirmed } = await apiConfirm(row.id, comment);
    message.success(row.confirmedByMe ? "Already confirmed" : "Confirmed");
    setRows((list) =>
      list.map((x) =>
        x.id === row.id
          ? {
              ...x,
              confirmedByMe: true,
              confirmedCount: Math.min((x.confirmedCount || 0) + 1, x.assigneeTotal || (x.assignees?.length || 0)),
            }
          : x
      )
    );
    if (allConfirmed) {
      message.info("Confirmed by all assignees");
      }
    } catch (error: any) {
      console.error("Failed to confirm task", error);
      const msg = error?.message;
      message.error(msg && msg !== "[object Object]" ? msg : "Failed to confirm task");
    }
  };

  const handleMyConfirm = async (row: RowItem) => {
    try {
      // 更新本地状态：按钮消失，名字变橘黄色
      setRows((list) =>
        list.map((x) =>
          x.id === row.id
            ? {
                ...x,
                myConfirmed: true,
              }
            : x
        )
      );
      message.success("Confirmed");
      
      // 可选：如果需要调用后端 API，可以在这里添加
      // await apiConfirm(row.id, `User confirmed task ${row.title}`);
    } catch (error: any) {
      console.error("Failed to confirm", error);
      message.error("Failed to confirm");
    }
  };

  const openNotice = (row: RowItem) => {
    setActiveRow(row);
    setVisibility("Selected");
    const options =
      row.participants?.map((p) => ({
        value: String(p.userId),
        label: p.name || `User ${p.userId}`,
      })) ?? [];
    setPeopleOptions(options);
    setPeople([]);
    setNoticeVisible(true);
  };

  return (
    <div style={{ padding: 24, background: "#f7f9fc", minHeight: "100vh" }}>
      <Card bordered={false} style={{ maxWidth: 1300, margin: "0 auto", boxShadow: "0 2px 10px rgba(0,0,0,.06)" }} bodyStyle={{ padding: 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Space direction="vertical" size={2}>
              <Title level={3} style={{ margin: 0 }}>My Task</Title>
              <Text type="secondary">Tasks related to your account. Confirm & report progress here.</Text>
            </Space>
          </Col>
        </Row>

        <Table<RowItem>
          rowKey="id"
          loading={loading}
          columns={columns as any}
          dataSource={rows}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: <Empty description="No tasks" /> }}
        />
      </Card>

      {/* Add Comment Modal */}
      <Modal
        title="Progress Update"
        visible={commentVisible}
        okText="Add"
        onOk={handleAddComment}
        onCancel={() => setCommentVisible(false)}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Select value={commentType} onChange={(v: CommentType) => setCommentType(v)}>
            <Option value="Routine">Routine</Option>
            <Option value="Apply Reschedule">Apply Reschedule</Option>
            <Option value="Apply Completion">Apply Completion</Option>
          </Select>
          <TextArea rows={5} placeholder="Write your comment…" value={commentContent} onChange={(e) => setCommentContent(e.target.value)} />
          <Upload multiple beforeUpload={() => false}
            onChange={({ fileList }) => setAttachments(fileList.map((f: any) => ({ name: f.name, url: f.thumbUrl || "#" })))}
            accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx,.doc,.docx"
          >
            <Button icon={<UploadOutlined />}>Attachment</Button>
          </Upload>
          <Text type="secondary">Accepted: .jpg/.png/.pdf/.excel/.docs/word</Text>
        </Space>
      </Modal>

      {/* Notice Modal */}
      <Modal
        title="Notification"
        visible={noticeVisible}
        okText="Confirm"
        onOk={async () => {
          if (!activeRow) return;
          try {
            const participantIds =
              activeRow.participants
                ?.map((p) => p.userId)
                .filter((id) => Number.isFinite(id) && id > 0) ?? [];
            const notifyIds =
              visibility === "All"
                ? participantIds
                : (people || [])
                    .map((val) => parseInt(String(val), 10))
                    .filter((id) => Number.isFinite(id) && id > 0);
            if (!notifyIds.length) {
              return message.warning("Please select at least one recipient.");
            }
            const text = `${activeRow.title} · ${commentType} comment is added.`;
            await apiCreateNotice(activeRow.id, text, notifyIds);
            setNoticeVisible(false);
            setPeople([]);
            message.success("Notice created");
          } catch (error: any) {
            console.error("Failed to create notice", error);
            const msg = error?.message;
            message.error(msg && msg !== "[object Object]" ? msg : "Failed to create notification");
          }
        }}
        onCancel={() => setNoticeVisible(false)}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>Creator: <Text strong>current account</Text> (fixed)</Text>
          <Select value={visibility} onChange={(v: "All" | "Selected") => setVisibility(v)}>
            <Option value="Selected">To Selected people</Option>
            <Option value="All">To All in this task</Option>
          </Select>
          {visibility === "Selected" && (
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select people"
              value={people}
              onChange={(vals) => setPeople(vals as string[])}
              filterOption={(input, option) =>
                String(option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {peopleOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          )}
          <TextArea rows={3} readOnly value={`${activeRow?.title || "Task"} · ${commentType} comment is added.`} />
        </Space>
      </Modal>
    </div>
  );
};

export default MyTaskPage;
